import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Braces,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
  Unlink,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';

/*
 * WYSIWYG-Editor für E-Mail-Vorlagen und Signaturen (TipTap/ProseMirror).
 * Bilder werden in die Bildablage hochgeladen (kein Base64 im Text); Variablen werden als {{schluessel}} eingefügt.
 * Der Server bereinigt das HTML beim Speichern zusätzlich.
 */

export interface EditorVariable {
  key: string;
  label: string;
  group: string;
}

const COLORS = ['#0f172a', '#475569', '#1d4ed8', '#047857', '#b45309', '#b91c1c', '#7c3aed'];

function Tool({ onClick, active, disabled, label, children }: { onClick: () => void; active?: boolean; disabled?: boolean; label: string; children: ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn('inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40', active && 'bg-accent text-foreground')}
    >
      {children}
    </button>
  );
}

function VariableMenu({ variables, onInsert }: { variables: EditorVariable[]; onInsert: (key: string) => void }) {
  const [open, setOpen] = useState(false);
  const groups = [...new Set(variables.map((v) => v.group))];
  return (
    <div className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border px-2 text-sm hover:bg-accent"
        aria-expanded={open}
      >
        <Braces className="size-4" /> Variable einfügen
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 max-h-80 w-72 overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-lg">
          {groups.map((g) => (
            <div key={g}>
              <p className="px-2 pb-0.5 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{g}</p>
              {variables
                .filter((v) => v.group === g)
                .map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onInsert(v.key);
                      setOpen(false);
                    }}
                    className="flex w-full flex-col items-start rounded px-2 py-1 text-left hover:bg-accent"
                  >
                    <span>{v.label}</span>
                    <code className="text-xs text-muted-foreground">{`{{${v.key}}}`}</code>
                  </button>
                ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Toolbar({ editor, variables }: { editor: Editor; variables: EditorVariable[] }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      link: e.isActive('link'),
      left: e.isActive({ textAlign: 'left' }),
      center: e.isActive({ textAlign: 'center' }),
      right: e.isActive({ textAlign: 'right' }),
      undo: e.can().undo(),
      redo: e.can().redo(),
    }),
  });
  const upload = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api<{ url: string }>('/mail-assets', { method: 'POST', form });
      editor.chain().focus().setImage({ src: r.url, alt: file.name.replace(/\.[^.]+$/, '') }).run();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setUploading(false);
    }
  };
  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Adresse des Links (https://… oder {{link}})', prev ?? 'https://');
    if (url === null) return;
    if (!url.trim()) return void editor.chain().focus().unsetLink().run();
    if (!/^(https?:\/\/|mailto:|tel:|\{\{\s*link\s*\}\})/i.test(url.trim())) return void toast.error('Nur https://, http://, mailto:, tel: oder {{link}}');
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b bg-muted/40 p-1">
      <Tool label="Fett" active={s.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="size-4" />
      </Tool>
      <Tool label="Kursiv" active={s.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="size-4" />
      </Tool>
      <Tool label="Unterstrichen" active={s.underline} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <Underline className="size-4" />
      </Tool>
      <Tool label="Durchgestrichen" active={s.strike} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="size-4" />
      </Tool>
      <span className="mx-1 h-5 w-px bg-border" />
      <Tool label="Überschrift" active={s.h2} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="size-4" />
      </Tool>
      <Tool label="Zwischenüberschrift" active={s.h3} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="size-4" />
      </Tool>
      <Tool label="Aufzählung" active={s.bullet} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="size-4" />
      </Tool>
      <Tool label="Nummerierung" active={s.ordered} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="size-4" />
      </Tool>
      <Tool label="Zitat" active={s.quote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="size-4" />
      </Tool>
      <Tool label="Trennlinie" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="size-4" />
      </Tool>
      <span className="mx-1 h-5 w-px bg-border" />
      <Tool label="Linksbündig" active={s.left} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
        <AlignLeft className="size-4" />
      </Tool>
      <Tool label="Zentriert" active={s.center} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
        <AlignCenter className="size-4" />
      </Tool>
      <Tool label="Rechtsbündig" active={s.right} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
        <AlignRight className="size-4" />
      </Tool>
      <span className="mx-1 h-5 w-px bg-border" />
      {COLORS.map((c) => (
        <button
          key={c}
          type="button"
          title={`Textfarbe ${c}`}
          aria-label={`Textfarbe ${c}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => (c === '#0f172a' ? editor.chain().focus().unsetColor().run() : editor.chain().focus().setColor(c).run())}
          className="mx-0.5 size-5 rounded-full border"
          style={{ backgroundColor: c }}
        />
      ))}
      <span className="mx-1 h-5 w-px bg-border" />
      <Tool label="Link" active={s.link} onClick={setLink}>
        <Link2 className="size-4" />
      </Tool>
      <Tool label="Link entfernen" disabled={!s.link} onClick={() => editor.chain().focus().unsetLink().run()}>
        <Unlink className="size-4" />
      </Tool>
      <Tool label="Bild einfügen" disabled={uploading} onClick={() => fileRef.current?.click()}>
        <ImagePlus className="size-4" />
      </Tool>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void upload(f);
        }}
      />
      <span className="mx-1 h-5 w-px bg-border" />
      <Tool label="Rückgängig" disabled={!s.undo} onClick={() => editor.chain().focus().undo().run()}>
        <Undo2 className="size-4" />
      </Tool>
      <Tool label="Wiederholen" disabled={!s.redo} onClick={() => editor.chain().focus().redo().run()}>
        <Redo2 className="size-4" />
      </Tool>
      {variables.length > 0 && (
        <div className="ml-auto">
          <VariableMenu variables={variables} onInsert={(k) => editor.chain().focus().insertContent(`{{${k}}}`).run()} />
        </div>
      )}
    </div>
  );
}

export function RichEditor({ value, onChange, variables = [], minHeight = '16rem', ariaLabel }: { value: string; onChange: (html: string) => void; variables?: EditorVariable[]; minHeight?: string; ariaLabel?: string }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, autolink: true, protocols: ['mailto', 'tel'] } }),
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({ allowBase64: false, inline: false }),
    ],
    content: value,
    editorProps: {
      attributes: { class: 'mail-editor prose-sm max-w-none px-4 py-3 focus:outline-none', ...(ariaLabel ? { 'aria-label': ariaLabel } : {}) },
      // Eingefügte Bilder als Base64 werden nicht übernommen (nur über „Bild einfügen“)
      handlePaste: (_view, event) => Boolean(event.clipboardData?.files.length),
      handleDrop: (_view, event) => Boolean((event as DragEvent).dataTransfer?.files.length),
    },
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });
  // Inhalt von außen ersetzen (z. B. „Standard laden“)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);
  if (!editor) return null;
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <Toolbar editor={editor} variables={variables} />
      <div style={{ minHeight }} className="cursor-text" onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
